// Claims-desk contracts (ADR 0010 §11), mirrored from the API. FNOL → advocacy
// → resolve. SENSITIVE-DATA DISCIPLINE: the console renders the flags
// (bodily-injury / third-party) and the non-sensitive claim_event.payload only —
// NEVER the detail_ref content (an access-controlled object-storage ref). The
// desk shows that a claim involves injury, not the injury detail.
export type ClaimStatus =
  | "REPORTED"
  | "DOCUMENTING"
  | "SUBMITTED"
  | "IN_PROGRESS"
  | "RESOLVED"
  | "WITHDRAWN";
export type ClaimOutcome = "PAID" | "PARTIALLY_PAID" | "REJECTED" | "WITHDRAWN";

export interface Claim {
  id: string;
  householdId: string;
  partyId: string | null;
  policyId: string;
  policyVersionId: string; // the version-in-force-at-loss
  carrierId: string;
  lineCode: string;
  carrierClaimRef: string | null;
  lossAt: string;
  reportedAt: string;
  status: ClaimStatus;
  outcome: ClaimOutcome | null;
  outcomeAmount: string | null;
  carrierFirstResponseAt: string | null;
  resolvedAt: string | null;
  satisfactionScore: number | null;
  involvesBodilyInjury: boolean;
  hasThirdParty: boolean;
  // detailRef is intentionally NOT typed for render — the console never shows it.
}

export type ClaimEventType =
  | "REPORTED"
  | "DOCUMENT_ADDED"
  | "SUBMITTED"
  | "STATUS_UPDATE"
  | "ADVOCACY_ACTION"
  | "CARRIER_RESPONSE"
  | "RESOLVED"
  | "WITHDRAWN";

export interface ClaimEvent {
  id: string;
  eventType: ClaimEventType;
  payload: Record<string, unknown> | null; // non-sensitive structured detail only
  occurredAt: string;
}

export interface ClaimDetail {
  claim: Claim;
  events: ClaimEvent[];
}

// The forward-only state machine — the single valid next state per status (the
// API enforces; the UI offers only this). IN_PROGRESS resolves instead.
export const NEXT_STATUS: Record<ClaimStatus, "DOCUMENTING" | "SUBMITTED" | "IN_PROGRESS" | null> =
  {
    REPORTED: "DOCUMENTING",
    DOCUMENTING: "SUBMITTED",
    SUBMITTED: "IN_PROGRESS",
    IN_PROGRESS: null,
    RESOLVED: null,
    WITHDRAWN: null,
  };

export const TERMINAL_STATUSES: ClaimStatus[] = ["RESOLVED", "WITHDRAWN"];

/** Advocacy-log entries the advisor can append (non-transition). */
export const APPENDABLE_EVENTS = ["DOCUMENT_ADDED", "ADVOCACY_ACTION", "CARRIER_RESPONSE"] as const;
export type AppendableEvent = (typeof APPENDABLE_EVENTS)[number];

export const RESOLVE_OUTCOMES = ["PAID", "PARTIALLY_PAID", "REJECTED"] as const;

export interface FnolInput {
  policyId: string;
  lossAt: string;
  partyId?: string;
  involvesBodilyInjury?: boolean;
  hasThirdParty?: boolean;
}
